<script setup>
import { inject, useSlots } from 'vue';
import { Launch16 } from '@carbon/icons-vue';
import CustomBrandPolicyWrapper from 'dashboard/components/CustomBrandPolicyWrapper.vue';
import { getHelpUrlForFeature } from '../../../../helper/featureHelper';
import { Button } from 'dashboard/components-next/polaris/ui/button';
import PageHeader from 'dashboard/components-next/polaris/cards/PageHeader.vue';
import ListToolbar from 'dashboard/components-next/polaris/cards/ListToolbar.vue';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    default: '',
  },
  linkText: {
    type: String,
    default: '',
  },
  featureName: {
    type: String,
    default: '',
  },
  searchPlaceholder: {
    type: String,
    default: '',
  },
});

const slots = useSlots();

const searchQuery = defineModel('searchQuery', { type: String, default: '' });

const helpURL = getHelpUrlForFeature(props.featureName);

// Inside the SettingsLayout mould the introduction and the search go to the top of the scrolling content, under
// the still header (the kit's SettingsArea); anywhere else they stay right here.
const introTarget = inject('settingsIntroTarget', null);
</script>

<!-- The kit's PageHeader (phase 9.1): title, "Learn more" (hidden on a custom-branded instance, as before) and the
     page's actions on the right; no bottom line when tabs follow. The description becomes the introduction line and
     the search becomes the kit's ListToolbar, with the page's count under it. No Back button (D-G2: the Topbar
     breadcrumb does that). -->
<template>
  <!-- Outside the mould (a page not moved to SettingsLayout yet) the frame pads the page: the header reaches
       back over that padding so it still spans the page. -->
  <PageHeader
    :title="title"
    :divider="!slots.tabs"
    :class="introTarget ? '' : '-mx-6 -mt-6 mb-2'"
  >
    <template v-if="helpURL && linkText" #learn>
      <CustomBrandPolicyWrapper :show-on-custom-branded-instance="false">
        <Button variant="secondary" size="sm" as-child>
          <a :href="helpURL" target="_blank" rel="noopener noreferrer">
            {{ linkText }}
            <Launch16 aria-hidden="true" />
          </a>
        </Button>
      </CustomBrandPolicyWrapper>
    </template>
    <template v-if="slots.actions" #actions>
      <slot name="actions" />
    </template>
  </PageHeader>
  <!-- [&>*]:min-w-0: the tabs may shrink to the page and scroll inside their list (a long row of tabs, like the
       inbox's at 1024 with tier 2 open, ran past the right edge) -->
  <div
    v-if="slots.tabs"
    class="flex shrink-0 items-end gap-3 px-6 [&>*]:min-w-0"
  >
    <slot name="tabs" />
  </div>
  <Teleport
    v-if="description || slots.description || searchPlaceholder || slots.count"
    defer
    :to="introTarget"
    :disabled="!introTarget"
  >
    <p
      v-if="description || slots.description"
      class="max-w-3xl type-polaris-body text-polaris-muted-foreground"
    >
      <slot name="description">{{ description }}</slot>
    </p>
    <div v-if="searchPlaceholder || slots.count" class="grid gap-3">
      <ListToolbar
        v-if="searchPlaceholder"
        v-model:search="searchQuery"
        :placeholder="searchPlaceholder"
      />
      <p
        v-if="slots.count"
        class="type-polaris-section-title"
        aria-live="polite"
      >
        <slot name="count" />
      </p>
    </div>
  </Teleport>
</template>
